import { update } from 'idb-keyval/dist/compat.js';
import { indexedDBJSONStorage } from './indexedDBStorage';
import { DEFAULT_CACHE_KEY } from '../services/queryParams';

// Before per-ticket partitioning, every ticket's task progress lived in this one
// IndexedDB record, and each tab rewrote the whole thing on every change — so tabs
// working on different tickets overwrote each other's unsynced entries.
export const LEGACY_TASKS_STORAGE_NAME = 'sow-tasks-storage';

// Each ticket persists to `${prefix}${issueKey}-${token}`.
export const TICKET_TASKS_STORAGE_PREFIX = 'sow-tasks-storage-';

// Pulls just this ticket's tasks out of the legacy shared record so work saved
// before the upgrade isn't lost. Returns null when there is nothing to migrate.
const readFromLegacyRecord = async (sowKey) => {
  const legacy = await indexedDBJSONStorage.getItem(LEGACY_TASKS_STORAGE_NAME);
  const legacyTasks = legacy?.state?.jobsTasks?.[sowKey];
  if (!legacyTasks) return null;
  return { ...legacy, state: { ...legacy.state, jobsTasks: { [sowKey]: legacyTasks } } };
};

const ticketTasksOf = (record, sowKey) => record?.state?.jobsTasks?.[sowKey] ?? [];

const modifiedAt = (task) => task.lastModified || 0;

const isFullySubmitted = (tasks) => tasks.length > 0 && tasks.every((task) => task.submitted);

// Per task, the more recently edited copy wins and ties keep the ticket's own record.
// A task only the legacy side has is adopted when it was actually edited there.
// Built-in tasks are matched by their key rather than their id: ids are positional, so a task
// inserted before Client QC (parts usage) moves Client QC and Travel from to other ids, and a
// copy that has not seen the insert would otherwise overwrite the wrong task. The winning copy
// keeps the id of the task it replaces.
const identityOf = (task) => (task.key ? `key:${task.key}` : `id:${task.id}`);

// A built-in task only the other side has (parts usage, inserted before Client QC) is adopted
// whatever id the other side gave it: that id can belong to a different task here. It goes in
// ahead of the first task that follows it on the other side, and ids are renumbered to stay
// positional like the parser's, so nothing collides and no task is dropped.
const placeAdopted = (tasks, adopted, otherTasks) => {
  const placed = adopted.reduce((merged, task) => {
    const followers = otherTasks.slice(otherTasks.findIndex((other) => identityOf(other) === identityOf(task)) + 1).map(identityOf);
    const at = merged.findIndex((candidate) => followers.includes(identityOf(candidate)));
    const position = at === -1 ? merged.length : at;
    return [...merged.slice(0, position), task, ...merged.slice(position)];
  }, tasks);
  return placed.map((task, index) => (task.id === index + 1 ? task : { ...task, id: index + 1 }));
};

const mergeTasksById = (ownTasks, otherTasks, { otherWinsTies = false } = {}) => {
  const ownIds = new Set(ownTasks.map((task) => task.id));
  const ownIdentities = new Set(ownTasks.map(identityOf));
  const otherByIdentity = new Map(otherTasks.map((task) => [identityOf(task), task]));
  const merged = ownTasks.map((ownTask) => {
    const otherTask = otherByIdentity.get(identityOf(ownTask));
    if (!otherTask) return ownTask;
    const otherIsNewer = modifiedAt(otherTask) > modifiedAt(ownTask);
    const isTieWonByOther = otherWinsTies && modifiedAt(otherTask) === modifiedAt(ownTask);
    return otherIsNewer || isTieWonByOther ? { ...otherTask, id: ownTask.id } : ownTask;
  });
  const isOtherOnly = (task) => !ownIdentities.has(identityOf(task)) && modifiedAt(task) > 0;
  const adopted = otherTasks.filter((task) => task.key && isOtherOnly(task));
  const otherOnly = otherTasks.filter((task) => !task.key && isOtherOnly(task) && !ownIds.has(task.id));
  const withOtherOnly = [...merged, ...otherOnly];
  return adopted.length > 0 ? placeAdopted(withOtherOnly, adopted, otherTasks) : withOtherOnly;
};

// A tab still running the pre-partitioning code keeps writing to the legacy record
// after another tab has already migrated this ticket. Merging per task means a newer
// edit on either side survives; a ticket that is already fully submitted is never
// reverted to unsubmitted work, whichever side holds the submission.
const reconcileWithLegacy = (own, legacy, sowKey) => {
  const ownTasks = ticketTasksOf(own, sowKey);
  if (isFullySubmitted(ownTasks)) return own;
  const legacyTasks = ticketTasksOf(legacy, sowKey);
  // An old-code tab that submitted the ticket keeps its earlier edit timestamps, so a
  // newer unsubmitted copy would win the merge and re-enable Submit: submitted is final.
  if (isFullySubmitted(legacyTasks)) return { ...own, state: { ...own.state, jobsTasks: { [sowKey]: legacyTasks } } };
  const merged = mergeTasksById(ownTasks, legacyTasks);
  return { ...own, state: { ...own.state, jobsTasks: { [sowKey]: merged } } };
};

const recordNameFor = (sowKey) => `${TICKET_TASKS_STORAGE_PREFIX}${sowKey}`;

const tasksDiffer = (first, second, sowKey) =>
  JSON.stringify(ticketTasksOf(first, sowKey)) !== JSON.stringify(ticketTasksOf(second, sowKey));

// Copies this ticket's legacy slice into its own record so the ticket stops depending on
// the shared legacy record, which a tab on old code can still overwrite. Done in a single
// IndexedDB read-write transaction, merging with whatever another client wrote to the
// ticket record in the meantime. Returns what ended up stored.
const migrateIntoTicketRecord = async (sowKey, legacy) => {
  let stored = legacy;
  await update(recordNameFor(sowKey), (current) => {
    const own = current ? JSON.parse(current) : null;
    stored = own ? reconcileWithLegacy(own, legacy, sowKey) : legacy;
    return JSON.stringify(stored);
  });
  return stored;
};

// Right after an in-app ticket switch the previous ticket's tasks are still in memory
// while the new ticket has none yet; writing that state would clobber the new ticket's
// record with the wrong ticket's data. With no ticket in the URL there is no per-ticket
// key to check against, so everything is written.
const carriesTasksForTicket = (value, sowKey) =>
  sowKey === DEFAULT_CACHE_KEY ||
  Object.prototype.hasOwnProperty.call(value?.state?.jobsTasks ?? {}, sowKey);

// The in-memory store can hold several tickets' tasks after in-app switches; each
// record must only ever contain its own ticket's.
const scopeToTicket = (value, sowKey) =>
  sowKey === DEFAULT_CACHE_KEY
    ? value
    : { ...value, state: { ...value.state, jobsTasks: { [sowKey]: value.state.jobsTasks[sowKey] } } };

// Storage for useSOWTasksStore. The persist `name` is fixed when the store is created,
// but HomeTab switches tickets with history.pushState and no reload, so the record is
// derived from the current ticket on every call and the `name` argument is ignored.
// Each load is reconciled with this ticket's slice of the legacy record, which is never
// written or deleted: other tickets' tabs still need to migrate from it.
export const createTicketTasksStorage = (getSowKey, canWrite = () => true) => ({
  getItem: async () => {
    const sowKey = getSowKey();
    const own = await indexedDBJSONStorage.getItem(recordNameFor(sowKey));
    const legacy = await readFromLegacyRecord(sowKey);
    if (!legacy) return own;
    if (!own) return migrateIntoTicketRecord(sowKey, legacy);

    const reconciled = reconcileWithLegacy(own, legacy, sowKey);
    return tasksDiffer(own, reconciled, sowKey) ? migrateIntoTicketRecord(sowKey, legacy) : reconciled;
  },
  setItem: async (_name, value) => {
    const sowKey = getSowKey();
    // `canWrite` is false after the ticket's record could not be read: what is in memory
    // is then not its work, and saving it would overwrite the unread record.
    if (!canWrite() || !carriesTasksForTicket(value, sowKey)) return;
    try {
      await indexedDBJSONStorage.setItem(recordNameFor(sowKey), scopeToTicket(value, sowKey));
    } catch (error) {
      console.error('Failed to save tasks:', error);
    }
  },
  removeItem: () => indexedDBJSONStorage.removeItem(recordNameFor(getSowKey())),
});

// Tasks persisted for several tickets at once, for views that need progress of tickets
// other than the one this tab has open (e.g. the Home list). The legacy record is read
// once for the whole batch and nothing is written, so tickets that were never opened get
// no record of their own. Reconciliation matches the store's, so both always agree.
// Returns `{ [sowKey]: tasks }` with [] for tickets with nothing stored.
export const readStoredTicketTasksByKey = async (sowKeys) => {
  const legacy = await indexedDBJSONStorage.getItem(LEGACY_TASKS_STORAGE_NAME);
  const entries = await Promise.all(sowKeys.map(async (sowKey) => {
    const own = await indexedDBJSONStorage.getItem(recordNameFor(sowKey));
    const legacyTasks = legacy?.state?.jobsTasks?.[sowKey];
    if (!legacyTasks) return [sowKey, ticketTasksOf(own, sowKey)];
    const reconciled = own ? reconcileWithLegacy(own, legacy, sowKey) : legacy;
    return [sowKey, ticketTasksOf(reconciled, sowKey)];
  }));
  return Object.fromEntries(entries);
};

// Combines the tasks read from a ticket's record with the same ticket's tasks that were
// worked on in memory while the record could not be read: per task the newer edit wins,
// a ticket already fully submitted on either side stays submitted.
export const mergeInMemoryTasks = (storedJobsTasks, memoryJobsTasks) =>
  Object.entries(memoryJobsTasks).reduce((merged, [sowKey, memoryTasks]) => {
    const storedTasks = merged[sowKey] ?? [];
    if (isFullySubmitted(storedTasks)) return merged;
    // Submitting in memory does not bump lastModified, so older stored edits would win.
    if (isFullySubmitted(memoryTasks)) return { ...merged, [sowKey]: memoryTasks };
    // With nothing stored, merging would keep only the edited tasks and drop the rest.
    if (storedTasks.length === 0) return { ...merged, [sowKey]: memoryTasks };
    // Memory is the work done after the failed read, so it also wins an equal edit time.
    return { ...merged, [sowKey]: mergeTasksById(storedTasks, memoryTasks, { otherWinsTies: true }) };
  }, storedJobsTasks);
